/**
 * Marquee (box) selection in the viewport, as pure functions: the rectangle a
 * drag spans, what it hits, and the selection it makes. By default a box
 * takes whatever it touches, as in Unreal, Unity and Blender; the strict
 * preference (Unreal's "Use Strict Box Selection") takes only what lies fully
 * inside. Shift adds and Ctrl subtracts, as in Blender.
 */
import type { EntityId } from "../../world/entity";
import type { SelectionState } from "./Selection";

/** CSS pixels in the viewport. */
export interface Rect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export type MarqueeMode = "replace" | "add" | "subtract";

/** Whether a candidate must touch the marquee or lie fully inside it. */
export type MarqueeCoverage = "intersect" | "contain";

export interface MarqueeCandidate {
  readonly id: EntityId;
  /** The entity's own bounds, projected. */
  readonly rect: Rect;
}

/** The rect between the press and the pointer, whichever way the drag went. */
export function marqueeRect(
  from: { readonly x: number; readonly y: number },
  to: { readonly x: number; readonly y: number },
): Rect {
  return {
    left: Math.min(from.x, to.x),
    top: Math.min(from.y, to.y),
    width: Math.abs(to.x - from.x),
    height: Math.abs(to.y - from.y),
  };
}

/** The candidates the marquee catches, in candidate order. */
export function marqueeHits(
  candidates: readonly MarqueeCandidate[],
  marquee: Rect,
  coverage: MarqueeCoverage,
): EntityId[] {
  const test = coverage === "contain" ? contains : intersects;
  return candidates.filter((candidate) => test(marquee, candidate.rect)).map((c) => c.id);
}

/** The selection after a marquee's hits replace, add to or subtract from it. */
export function selectionAfterMarquee(
  current: SelectionState,
  hits: readonly EntityId[],
  mode: MarqueeMode,
): SelectionState {
  switch (mode) {
    case "replace":
      return { ids: hits, active: hits.at(-1) ?? null };
    case "add": {
      if (hits.length === 0) return current;
      const ids = [...current.ids.filter((id) => !hits.includes(id)), ...hits];
      return { ids, active: hits.at(-1) ?? null };
    }
    case "subtract": {
      const ids = current.ids.filter((id) => !hits.includes(id));
      const active =
        current.active !== null && ids.includes(current.active)
          ? current.active
          : (ids.at(-1) ?? null);
      return { ids, active };
    }
  }
}

/** Rects that only share an edge don't overlap. */
function intersects(a: Rect, b: Rect): boolean {
  return (
    a.left < b.left + b.width &&
    b.left < a.left + a.width &&
    a.top < b.top + b.height &&
    b.top < a.top + a.height
  );
}

function contains(outer: Rect, inner: Rect): boolean {
  return (
    inner.left >= outer.left &&
    inner.top >= outer.top &&
    inner.left + inner.width <= outer.left + outer.width &&
    inner.top + inner.height <= outer.top + outer.height
  );
}
